import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorFromResponse, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type JobKickoff = components['schemas']['JobKickoffOut']

/** Per-run LLM routing mode picked in a factory form (mirrors the backend
 * ``llm_provider`` field on every ``*StartIn``). ``auto`` follows the global
 * default (some factories already route to the local Ollama model); ``cloud``
 * forces the configured cloud provider; ``local`` forces the local Ollama model. Kept as a
 * local augmentation until the OpenAPI typegen is re-run. */
export type LlmProviderMode = 'auto' | 'cloud' | 'local'

/** Cost estimate shape embedded in each catalog row (decisão 31 + 55).
 *
 * Augmented locally because the OpenAPI typegen hasn't been re-run since
 * Phase A added these fields; same pattern as ``thumbnail_style`` /
 * ``stock_source`` on ``ViralShortsInput``.
 *
 * ``source`` discriminates the two paths:
 *   - ``'channel_history'`` — aggregated from this channel's
 *     ``llm_api_calls`` over the last 90 days; trustworthy real spend.
 *   - ``'default'`` — global fallback from ``config/settings.py`` because
 *     samples<5 in at least one tier OR no ``channel`` was passed to the
 *     catalog endpoint. The UI surfaces this with a "estimativa global"
 *     hint on the chip's tooltip (decisão 59). */
export interface CostEstimate {
  standard_avg_usd: number
  high_avg_usd: number
  samples: number
  source: 'channel_history' | 'default'
}

export type FactoryCatalogEntry = components['schemas']['FactoryCatalogEntryOut'] & {
  /** Tiers the factory's :class:`QualityHooks` accept (decisão 26).
   * ``['standard']`` for opt-out factories; ``['standard', 'high']`` for
   * those with real hooks (currently only ``viral_shorts`` post-Phase-B). */
  supported_tiers: ('standard' | 'high')[]
  cost_estimate: CostEstimate
}

/**
 * Pull the platform's factory catalog (slug, kind, title, description,
 * format, hue, icon, etc.) from the backend.
 *
 * Source of truth lives in the ``factories`` DB table — disabling a
 * factory there hides the tile here without a frontend deploy. The
 * selector intersects this list with the local ``FORM_BY_SLUG`` map
 * so a factory only renders when both sides have it.
 *
 * ``channel`` (optional, decisão 55) triggers per-channel ``cost_estimate``
 * aggregation on the backend. Pass the active channel slug from forms
 * that show the HQ chip so the cost reflects real spend on that canal.
 * Forms outside the per-channel context (selector grid) can omit it and
 * get global defaults — separately cached so flipping channels doesn't
 * blow away the no-channel cache.
 */
export function useFactoryCatalog(channel?: string | null) {
  return useQuery({
    queryKey: queryKeys.factories.catalog(channel),
    queryFn: async (): Promise<FactoryCatalogEntry[]> => {
      // ``channel`` was added to ``/catalog`` in Phase D but the
      // OpenAPI typegen hasn't been re-run — the generated ``params``
      // type still only lists ``include_disabled``. Cast through
      // ``unknown`` so we can pass the param without disabling
      // typechecking on the rest of the call. Drop the cast after
      // the next typegen pass.
      const { data } = await api.GET('/api/v1/factories/catalog', {
        params: (channel ? { query: { channel } } : {}) as unknown as never,
      })
      return (data?.data ?? []) as FactoryCatalogEntry[]
    },
    // Catalog rarely changes — once per session is enough for dev,
    // and the backend write path (DB UPDATE) doesn't notify the
    // frontend anyway. 5min keeps it fresh enough for ops to flip
    // ``available`` and see the change on the next poll. The cost
    // estimate is also slow-moving (90-day window) so the same TTL
    // applies to the per-channel variant.
    staleTime: 5 * 60_000,
  })
}

/** Body schema generated from the FastAPI route. ``thumbnail_style`` was
 * added with the thumbnail-v2 carousel (2026-04-26) — augment locally
 * until the OpenAPI typegen is re-run. ``stock_source`` was added with
 * the Pixabay alternative (2026-05-03). ``quality_tier`` was added with
 * the Quality Tier feature (Phase A) but the typegen hasn't been
 * re-run; defaults to ``'standard'`` on the backend when omitted. */
export type ViralShortsInput = components['schemas']['ViralShortsStartIn'] & {
  thumbnail_style?: string
  stock_source?: 'pexels' | 'pixabay'
  quality_tier?: 'standard' | 'high'
  llm_provider?: LlmProviderMode
}

/** Body for ``POST /factories/pov-lives/start``. Defined locally — the
 * OpenAPI typegen hasn't been re-run since this factory shipped. Mirror
 * the Pydantic ``POVLivesStartIn`` exactly.
 *
 * POV Lives is **long-form** (5–20 min, 16:9), AI stills + Ken Burns +
 * chapter cards, second-person life narrative. Field shape mirrors
 * ``AtlasInput`` (theme/custom_prompt/channel/count/language/music_mood/
 * end_text/tone/force) plus POV-specific knobs. No I2V (Veo/LTX) — long
 * runtimes make per-clip generation cost-prohibitive and the genre's
 * canonical look is exactly still + slow zoom + pan. */
export interface POVLivesInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: 'tense' | 'melancholic' | 'dramatic' | 'hopeful' | null
  music_mood?: 'tense' | 'calm' | 'epic' | 'mysterious' | 'melancholic'
  force?: boolean
  duration_minutes?: 5 | 10 | 15 | 20
  chapter_count?: 8 | 12 | 16 | 20 | 24 | 32
  image_style?: string
  image_provider?: 'pollinations' | 'hf'
  voice?: string
  seed?: number | null
  extra_direction?: string
  llm_provider?: LlmProviderMode
}

/** Body for ``POST /factories/reddit-stories/start``. Defined locally — the
 * OpenAPI typegen hasn't been re-run since this factory shipped. Mirror the
 * Pydantic ``RedditStoriesStartIn`` exactly. */
export interface RedditStoriesInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: 'dramatic' | 'deadpan' | 'outraged' | 'amused' | null
  music_mood?: string | null
  force?: boolean
  stock_source?: 'pexels' | 'pixabay' | null
  subtitle_position?: 'center' | 'bottom' | null
  source_mode?: 'fabricated' | 'inspired'
  subreddit_override?:
    | 'aita'
    | 'tifu'
    | 'malicious_compliance'
    | 'pro_revenge'
    | 'entitled_parents'
    | 'choosing_beggars'
    | 'relationship_advice'
    | null
  num_segments?: number
  chat_bubbles_enabled?: boolean
  anonymize_names?: boolean
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  subtitle_style?: string | null
  subtitle_active_color?: string | null
  thumbnail_style?: string | null
  tts_provider?: string | null
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  channel_tag?: string | null
  video_width?: number | null
  video_height?: number | null
  video_fps?: number | null
}

export type QuizVideoType = 'trivia' | 'choice'

/** Body for ``POST /factories/quiz/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``QuizStartIn`` exactly. */
export interface QuizInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: 'formal' | 'curious' | 'sarcastic' | null
  music_mood?: string | null
  force?: boolean
  num_questions?: number
  seconds_per_question?: number
  difficulty?: 'easy' | 'medium' | 'hard'
  options_per_question?: number
  /** ``trivia`` = one right answer; ``choice`` = dilemmas with no right answer
   * (3-5 rounds, 2-3 options, 3-6 s countdown; no ``both`` language). */
  video_type?: QuizVideoType
  choice_mode?: 'would_you_rather'
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  subtitle_style?: string | null
  subtitle_active_color?: string | null
  thumbnail_style?: string | null
  tts_provider?: string | null
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  channel_tag?: string | null
  video_width?: number | null
  video_height?: number | null
  video_fps?: number | null
}

/** Body for ``POST /factories/etymology/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``EtymologyStartIn`` exactly. ``options_per_word`` is intentionally absent —
 * fixed at 3 by the backend (quiz mode only) and not exposed as a knob.
 * ``presentation_mode`` defaults to ``definition`` (direct teaching) and can
 * be flipped to ``quiz`` from the form's advanced section. */
export interface EtymologyInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: 'formal' | 'curious' | 'sarcastic' | null
  music_mood?: string | null
  force?: boolean
  num_words?: number
  seconds_per_word?: number
  presentation_mode?: 'definition' | 'quiz'
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  subtitle_style?: string | null
  subtitle_active_color?: string | null
  tts_provider?: string | null
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  channel_tag?: string | null
  video_width?: number | null
  video_height?: number | null
  video_fps?: number | null
}

/** Body for ``POST /factories/ranked/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``RankedStartIn`` exactly. ``data_source`` is per-theme (declared in the
 * theme JSON) and therefore not exposed on the form. */
export interface RankedInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: string | null
  music_mood?: string | null
  force?: boolean
  item_count?: number
  target_duration?: number
  stock_source?: 'pexels' | 'pixabay' | null
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  subtitle_style?: string | null
  subtitle_active_color?: string | null
  thumbnail_style?: string | null
  tts_provider?: string | null
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  channel_tag?: string | null
  video_width?: number | null
  video_height?: number | null
  video_fps?: number | null
}

/** Body for ``POST /factories/product-review/start``. Defined locally — the
 * OpenAPI typegen hasn't been re-run since this route shipped. Mirror the
 * Pydantic ``ProductReviewStartIn`` exactly. The bookmarklet path
 * (``ingested``) and the paste-HTML escape hatch (``paste_html`` /
 * ``paste_url``) are still accepted by the route and wired here as
 * optional overrides — the v1 form only sends ``url``. */
export interface ProductReviewInput {
  url: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both'
  end_text?: string | null
  music_mood?: 'energetic' | 'neutral' | 'calm'
  force?: boolean
  tone?: 'enthusiastic' | 'neutral' | 'critical' | 'comedic'
  focus?: 'features' | 'benefits' | 'unboxing' | 'comparison'
  duration?: number
  mix_mode?: 'product_first' | 'mix' | 'pexels_only'
  subtitle_position?: 'top' | 'center' | 'bottom'
  support_texts?: boolean
  mention_price?: boolean
  mention_rating?: boolean
  custom_cta?: string
  custom_prompt?: string
  tts_voice?: string | null
  tts_provider?: string | null
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  channel_tag?: string | null
  /** Optional overrides not exposed by the v1 form. */
  paste_html?: string
  paste_url?: string
}

/** Body for ``POST /factories/cortes/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this route shipped. Mirror the Pydantic
 * ``CortesStartIn`` exactly. The first media-processing factory: one source
 * video (URL or a prior ClipCreator upload path) is transcribed, the model
 * picks the best windows, and each becomes a 9:16 short. Submitting a source
 * is itself the rights assertion — the separate checkbox was dropped on
 * 2026-08-15. */
export interface CortesInput {
  channel?: string | null
  source_url?: string
  source_path?: string
  language?: 'pt-br' | 'en'
  max_clips?: number
  min_clip_seconds?: number
  max_clip_seconds?: number
  reframe_mode?: 'center' | 'face_track' | 'smart'
  subtitle_style?: string
  subtitle_active_color?: string
  custom_prompt?: string
  force?: boolean
  llm_provider?: LlmProviderMode
}

/** Body for ``POST /factories/football/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``FootballStartIn`` exactly. Data source is the ``football-feed`` lib
 * (scraping UOL + ogol), so there are no API-key knobs. */
export interface FootballInput {
  /** Active channel slug. Falls back to user's default server-side when null. */
  channel?: string | null
  language?: 'pt-br' | 'en' | 'both'
  music_mood?: string
  force?: boolean

  /** Video format slug: ``top3_standings``, ``next_round_preview`` or
   * ``season_insight`` (card #103). */
  video_type?: string
  /** Card #103 — recorte da temporada, só lido quando
   * ``video_type === 'season_insight'``. Vazio = o fetcher escolhe o
   * primeiro tema com dado disponível. */
  theme?: string
  /** Season year; null/omitted = settings.football_default_season (currently 2026). */
  season?: number | null
  /** Specific round; null/omitted = current round per the lobby snapshot. */
  round?: number | null
  /** Target duration in seconds. Backend clamps to [15, 120]. */
  duration?: number
  /** Quality tier — STANDARD keeps the legacy Sonnet path; HIGH enables
   * extended thinking on the script + channel historical examples
   * (decisão 19 + Phase F). Backend defaults to ``'standard'`` when
   * omitted; the QualityTierSelector self-hides on the form when the
   * factory's hooks opt out. */
  quality_tier?: 'standard' | 'high'
  /** Issue #24 Feat #8 — render the top-6 standings as an overlay during
   * the outro + closing AND have the narration mention the table.
   * Backend default is True; setting False drops BOTH the overlay AND
   * the narrated mention so the closing focuses on the top scorer. */
  show_standings_outro?: boolean
  llm_provider?: LlmProviderMode
}

/** Body for ``POST /factories/nasa-apod/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``NasaApodStartIn`` exactly. */
export interface NasaApodInput {
  date?: string | null
  language?: 'pt-br' | 'en' | 'both'
  channel?: string | null
  music_mood?: string
  end_text?: string
  tone?: 'formal' | 'curious' | 'sarcastic' | null
  force?: boolean
  /** Per-request voice override. Empty / null = use channel/global default. */
  tts_voice_pt?: string | null
  tts_voice_en?: string | null
  llm_provider?: LlmProviderMode
}

/** Body for ``POST /factories/mirage/start``. Defined locally — the OpenAPI
 * typegen hasn't been re-run since this factory shipped. Mirror the Pydantic
 * ``MirageStartIn`` exactly. */
export interface MirageInput {
  topic: string
  scene_count?: number
  language?: 'pt-br' | 'en'
  image_style?: string
  image_provider?: 'pollinations' | 'hf'
  image_model?: string
  video_provider?: 'veo' | 'ltx' | 'fal'
  video_model?: string
  video_aspect?: 'shorts' | 'video'
  voice?: string
  seed?: number | null
  extra_direction?: string
  llm_provider?: LlmProviderMode
}

/**
 * POST /factories/viral-shorts/start — kicks off a generation job. Returns
 * the opaque ``job_id`` the UI uses to poll `/jobs/{id}` for live status.
 * No optimistic update: generation takes minutes, not milliseconds.
 */
export function useStartViralShorts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ViralShortsInput): Promise<JobKickoff> => {
      const { data } = await api.POST('/api/v1/factories/viral-shorts/start', {
        body: input,
      })
      if (!data) throw new Error('no response from factory kickoff')
      return data.data
    },
    onSuccess: () => {
      // Jobs list will have a new row; the Projects grid will get new
      // entries as the pipeline completes each language pass.
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

export type AtlasInput = components['schemas']['AtlasStartIn'] & {
  llm_provider?: LlmProviderMode
}

/**
 * POST /factories/atlas/start — kicks off an Atlas (animated maps) job.
 * Same kickoff/polling shape as Viral Shorts; the backend renders a clip
 * of countries being highlighted while a generated script narrates.
 */
export function useStartAtlas() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: AtlasInput): Promise<JobKickoff> => {
      const { data } = await api.POST('/api/v1/factories/atlas/start', {
        body: input,
      })
      if (!data) throw new Error('no response from factory kickoff')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/pov-lives/start — kicks off a POV Lives (long-form) job.
 * Same kickoff/poll shape as the other factories; differs only in the
 * body schema. Bypasses the typed openapi-fetch path until the OpenAPI
 * typegen catches up to the new route.
 */
export function useStartPovLives() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: POVLivesInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/pov-lives/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (POV Lives)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from POV Lives kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/quiz/start — kicks off a Quiz job. Same kickoff/polling
 * shape as Atlas / viral-shorts; defined here with ``authFetch`` because
 * the OpenAPI typegen hasn't been re-run since the route shipped.
 */
export function useStartQuiz() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: QuizInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/quiz/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Quiz)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from quiz kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/** Body for ``POST /api/v1/factories/code-levels/start``. Hand-rolled because
 * the OpenAPI typegen hasn't been re-run since this factory shipped. Mirror
 * the Pydantic ``CodeLevelsStartIn`` exactly. */
export interface CodeLevelsInput {
  theme: string
  channel?: string | null
  count?: number
  language?: 'pt-br' | 'en' | 'both' | null
  custom_prompt?: string
  end_text?: string | null
  tone?: string | null
  music_mood?: string | null
  force?: boolean
  difficulty?: 'easy' | 'medium' | 'hard'
  quality_tier?: 'standard' | 'high'
  llm_model?: string | null
  llm_provider?: LlmProviderMode
  channel_tag?: string | null
  video_width?: number | null
  video_height?: number | null
  video_fps?: number | null
}

/**
 * POST /factories/code-levels/start — Code Levels (Junior vs Senior typed code).
 * Same kickoff shape as the rest; ``authFetch`` until typegen catches up.
 */
export function useStartCodeLevels() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CodeLevelsInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/code-levels/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Code Levels)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from code-levels kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/reddit-stories/start — kicks off a Reddit Stories job. Same
 * kickoff/poll shape as Quiz / NASA APOD; ``authFetch`` because the OpenAPI
 * typegen hasn't been re-run since this route shipped.
 */
export function useStartRedditStories() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RedditStoriesInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/reddit-stories/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Reddit Stories)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from reddit_stories kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/etymology/start — kicks off an Etymology job. Same
 * kickoff/poll shape as Quiz / NASA APOD; ``authFetch`` because the OpenAPI
 * typegen hasn't been re-run since this route shipped.
 */
export function useStartEtymology() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: EtymologyInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/etymology/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Etymology)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from etymology kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/ranked/start — kicks off a Ranked (Top N countdown) job.
 * Same kickoff/poll shape as the other factories; ``authFetch`` because
 * OpenAPI typegen hasn't been re-run since this route shipped.
 */
export function useStartRanked() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RankedInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/ranked/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Ranked)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from ranked kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/product-review/start — kicks off a Product Review job.
 * Same kickoff/poll shape as the other factories; ``authFetch`` because
 * the OpenAPI typegen hasn't been re-run since this route shipped.
 */
export function useStartProductReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProductReviewInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/product-review/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Product Review)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from Product Review kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/cortes/start — kicks off a Cortes (podcast → Shorts) job.
 * Same kickoff/poll shape as the other factories; ``authFetch`` because the
 * OpenAPI typegen hasn't been re-run since this route shipped.
 */
export function useStartCortes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CortesInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/cortes/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Cortes)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from Cortes kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/nasa-apod/start — kicks off a NASA APOD job. Same
 * kickoff/poll shape as the other factories; ``authFetch`` because the
 * OpenAPI typegen hasn't been re-run since this route shipped.
 */
export function useStartNasaApod() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: NasaApodInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/nasa-apod/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (NASA APOD)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from NASA APOD kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/football/start — kicks off a Brasileirão (top3_standings)
 * job. Same kickoff/poll shape as the other factories. Data is sourced by
 * the ``football-feed`` lib (UOL + ogol) inside the backend pipeline.
 */
export function useStartFootball() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: FootballInput): Promise<JobKickoff> => {
      const res = await authFetch('/api/v1/factories/football/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Football)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from football kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * POST /factories/mirage/start — kicks off a Mirage job. Same kickoff/poll
 * shape as viral-shorts; differs only in the body schema.
 */
export function useStartMirage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: MirageInput): Promise<JobKickoff> => {
      // We bypass the typed openapi-fetch path for now because the local
      // OpenAPI types haven't been regenerated to include the mirage route.
      // Revert this to ``api.POST`` once the typegen catches up.
      const res = await authFetch('/api/v1/factories/mirage/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'include',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao iniciar a geração (Mirage)')
      }
      const json = (await res.json()) as { data?: JobKickoff }
      if (!json.data) throw new Error('no response from mirage kickoff')
      return json.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

export type SuggestPromptFactory =
  | 'viral_shorts'
  | 'atlas'
  | 'etymology'
  | 'quiz'
  | 'pov_lives'
  | 'reddit_stories'
  | 'ranked'

export interface SuggestPromptInput {
  factory: SuggestPromptFactory
  language: 'pt-br' | 'en' | 'both'
  channel?: string | null
  /** Formato com sugestão própria (quiz ``choice``: temas de dilemas). */
  video_type?: string | null
}

/**
 * POST /factories/{factory}/suggest-prompt — returns a fresh, LLM-generated
 * ``custom_prompt`` grounded in the factory's themes + the channel's recent
 * prompt history. Used by the "Gerar" button next to the Prompt customizado
 * textarea on each subfactory form. Not cached: each click should hit the LLM
 * so the user can re-roll.
 */
export function useSuggestPrompt() {
  return useMutation({
    mutationFn: async (input: SuggestPromptInput): Promise<string> => {
      const { factory, language, channel, video_type } = input
      const res = await authFetch(
        `/api/v1/factories/${factory}/suggest-prompt`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Só quando existe: o schema do backend é ``extra="forbid"``, e um
          // backend sem o campo recusaria a chave até de quem não a usa.
          body: JSON.stringify({ language, channel: channel ?? null, ...(video_type ? { video_type } : {}) }),
          credentials: 'include',
        },
      )
      if (!res.ok) {
        // Backend speaks RFC 9457 (problem+json): only its ``detail`` reaches
        // the UI — never the raw body, which can carry SQL stacks or stack
        // traces from unhandled paths.
        throw await apiErrorFromResponse(res, 'Falha ao sugerir prompt.')
      }
      const json = (await res.json()) as { data?: { prompt?: string } }
      const prompt = json.data?.prompt ?? ''
      if (!prompt) throw new Error('Resposta vazia do servidor')
      return prompt
    },
  })
}
